import { useContext, useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Header from 'widgets/header';
import Confirmation from 'widgets/confirmation';
import UserDetailsHeader from './userDetailsHeader';
import Options from './options';
import { ModalMobile, ModalDesktop } from '@acko-tech/building-blocks.ui.modal';
import { getImageBasePath } from 'utils/generics';
import { MPosContext } from 'utils/context';
import {
    SegmentEventNameTypes,
    SegmentEventPageTypes,
    SegmentEventPages,
    PRODUCT,
    SUB_PRODUCT_NAME,
    SegmentEventActionTypes,
} from 'utils/segment/constants';
import { useSegmentViewEvents } from 'hooks/useSegmentView';
import { useResponsiveDevice } from 'hooks/useResponsiveDevice';
import { useFetchParams } from 'hooks/useFetchParams';
import { Partners, Permissions } from 'utils/constants';
import * as Styles from './styles';
import {
    setPartnerNavState,
    PARTNER_NAV_STATES,
} from 'utils/localStorage/partnerStorage';

// helpers
import { getSegmentProps } from './helpers';
import sendSegmentEvent from 'utils/segment';

function Hamburger() {
    const router = useRouter();

    const { lob } = useFetchParams();

    const { isMobile } = useResponsiveDevice();

    const {
        permissionsMap,
        permissions,
        partnerId,
        loggedUserRole,
        legalDetailsUploadedStatus,
        userDetails,
        userAccessList,
    } = useContext(MPosContext);

    const purchaseJourney = permissions.purchase_journey;
    const uniquePartnerIds = new Set(
        (userAccessList || [])
            .map((access: any) => access?.partner_id)
            .filter(Boolean)
    );
    const hasMultiplePartners = uniquePartnerIds.size > 1;
    const { setShowHamburger } = useContext(MPosContext);

    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [isSwitchingPartner, setIsSwitchingPartner] = useState(false);

    /**
     * view segment hook
     */

    useSegmentViewEvents(SegmentEventNameTypes.VIEW, {
        page_url: typeof window !== 'undefined' ? window.location.href : '',
        page_type: SegmentEventPageTypes.MAIN_PAGE,
        view_name: SegmentEventPages.VIEW_HAMBURGER_SCREEN,
        product: PRODUCT,
        sub_product_name: SUB_PRODUCT_NAME,
        platform: isMobile ? 'mweb' : 'web',
        referrer: document.referrer || '',
        device: navigator.userAgent || '',
        partner: partnerId,
    });

    const onClick = async (id: string) => {
        if (id === 'switch_partner') {
            setIsSwitchingPartner(true);
            try {
                setPartnerNavState(PARTNER_NAV_STATES.SELECTING);
                await router.push('/partners');
            } finally {
                setShowHamburger(false);
                setIsSwitchingPartner(false);
            }
            return;
        }

        setShowHamburger(false);
        switch (id) {
            case 'transaction':
                router.push(`/${lob}/${partnerId}/transactions`);
                break;
            case 'contact_us':
                router.push(`/${lob}/${partnerId}/contact-us`);
                break;
            case 'legal_tnc':
                router.push(`/${lob}/${partnerId}/legal-tnc`);
                break;
            case 'commissions_earned':
                router.push(`/${lob}/${partnerId}/commissions-earned`);
                break;
            case 'commissions_earned_v1':
                router.push(`/${lob}/${partnerId}/commissions-earned-v1`);
                break;
            case 'report_dashboard':
                router.push(`/${lob}/${partnerId}/report-dashboard`);
                break;
            case 'details':
                router.push(`/${lob}/${partnerId}/details`);
                break;
            case 'wallet_transactions':
                router.push(`/${lob}/${partnerId}/wallet-transactions`);
                break;
            default:
                router.push(`/${lob}/${partnerId}/manage-dealers`);
        }
        /**
         * Segment event
         */

        const segmentProps = getSegmentProps(id);

        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            { ...segmentProps, action_type: SegmentEventActionTypes.TAP_BTN },
            { userDetails, isMobile }
        );
    };

    const logout = (e: any, status: 'yes' | 'no') => {
        e.preventDefault();
        setShowLogoutModal(false);
        if (status === 'yes') {
            window.location.href = '/logout';
        }
    };

    const Modal: any = isMobile ? ModalMobile : ModalDesktop;

    if (isSwitchingPartner) {
        return (
            <Styles.Wrapper>
                <Styles.SwitchingText>
                    Switching partner...
                </Styles.SwitchingText>
            </Styles.Wrapper>
        );
    }

    return (
        <>
            <Styles.Wrapper>
                <Header />
                <UserDetailsHeader />
                <Styles.OptionWrapper>
                    {hasMultiplePartners && (
                        <>
                            <Options
                                id={'switch_partner'}
                                icon={'/icons/refresh-icon.svg'}
                                title={'Switch brand'}
                                onClick={onClick}
                            />
                            <Styles.Hr />
                        </>
                    )}
                    {!permissions.permission_list.includes(
                        'access_only_auto_commercial_flow'
                    ) && (
                        <>
                            <Options
                                id={'transaction'}
                                icon={'/icons/transaction.svg'}
                                title={'My transactions'}
                                onClick={onClick}
                            />
                            <Styles.Hr />
                        </>
                    )}
                    {permissionsMap[Permissions.COMMISSIONS_EARNED] &&
                        purchaseJourney === 'v1' && (
                            <>
                                <Options
                                    id={'commissions_earned'}
                                    icon={'/icons/commissions_light.svg'}
                                    title={'Sales Report'}
                                    onClick={onClick}
                                />
                                <Styles.Hr />
                            </>
                        )}
                    {permissionsMap[Permissions.COMMISSIONS_EARNED_V1] &&
                        purchaseJourney === 'v1' && (
                            <>
                                <Options
                                    id={'commissions_earned_v1'}
                                    icon={'/icons/commissions_light.svg'}
                                    title={'Sales Report'}
                                    onClick={onClick}
                                />
                                <Styles.Hr />
                            </>
                        )}
                    {permissionsMap[Permissions.REPORT_DASHBOARD] &&
                        purchaseJourney === 'v1' && (
                            <>
                                <Options
                                    id={'report_dashboard'}
                                    icon={'/icons/commissions_light.svg'}
                                    title={'Sales Dashboard'}
                                    onClick={onClick}
                                />
                                <Styles.Hr />
                            </>
                        )}
                    {(permissionsMap[Permissions.ADD_SUB_DEALERS] ||
                        permissionsMap[Permissions.REMOVE_SUB_DEALERS]) && (
                        <>
                            <Options
                                id={'add-remove'}
                                icon={'/icons/user.svg'}
                                title={'Add/remove sub-dealer'}
                                onClick={onClick}
                            />
                            <Styles.Hr />
                        </>
                    )}
                    <>
                        <Options
                            id={'contact_us'}
                            icon={'/icons/contactUs.svg'}
                            title={'Contact us'}
                            onClick={onClick}
                        />
                        <Styles.Hr />
                    </>
                    {permissionsMap[Permissions.PROFILE_DETAILS_SECTION] && (
                        <>
                            <Options
                                id={'details'}
                                icon={'/icons/checkClaim.svg'}
                                title={'Details'}
                                onClick={onClick}
                            />
                            <Styles.Hr />
                        </>
                    )}
                    {permissionsMap[Permissions.WALLET_TRANSACTIONS] && (
                        <>
                            <Options
                                id={'wallet_transactions'}
                                icon={'/icons/checkClaim.svg'}
                                title={'Wallet transactions'}
                                onClick={onClick}
                            />
                        </>
                    )}
                    {partnerId === Partners.OPPO &&
                        !legalDetailsUploadedStatus &&
                        loggedUserRole === 'dealer' && (
                            <>
                                <Styles.Hr />
                                <Options
                                    id={'legal_tnc'}
                                    icon={'/icons/tnc.png'}
                                    title={'Legal T&C'}
                                    onClick={onClick}
                                />
                            </>
                        )}
                </Styles.OptionWrapper>
                <Styles.LogoutFlex
                    onClick={() => setShowLogoutModal((s) => !s)}
                >
                    <Styles.LogoutIcon>
                        <Image
                            width={'24px'}
                            height={'24px'}
                            alt={''}
                            src={getImageBasePath('/icons/logout.svg')}
                        />
                    </Styles.LogoutIcon>
                    <Styles.Logout>Logout</Styles.Logout>
                </Styles.LogoutFlex>
            </Styles.Wrapper>
            <Modal
                header={<></>}
                show={showLogoutModal}
                onHide={() => setShowLogoutModal(false)}
                padding={'20px'}
                wrapperStyles={`border-radius: 16px 16px 0px 0px;`}
                showOverlayBackground={true}
            >
                <Confirmation
                    image={'/images/alert.svg'}
                    text={`Are you sure you want to log out?`}
                    onClick={logout}
                />
            </Modal>
        </>
    );
}

export default Hamburger;
